/**
 * In-memory store for paused live-capture state keyed by sessionId.
 * Lets clinicians pause visit A, open visit B, then return and resume A
 * with transcript + audio segments preserved until Stop → SOAP.
 *
 * Lightweight metadata is also mirrored to sessionStorage so elapsed time /
 * options survive soft remounts (blobs stay in-memory / S3 chunks).
 */

/** @type {Map<string, object>} */
const parkedBySessionId = new Map();

/** @type {Set<(ids: string[]) => void>} */
const listeners = new Set();

const META_STORAGE_KEY = "vbot_parked_capture_meta";
const FINISHED_STORAGE_KEY = "vbot_finished_capture_sessions";

/** Sessions whose live capture already finished (Stop / SOAP). Do not re-park. */
const finishedCaptureSessionIds = new Set();

function readFinishedIds() {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(FINISHED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeFinishedIds() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      FINISHED_STORAGE_KEY,
      JSON.stringify(Array.from(finishedCaptureSessionIds))
    );
  } catch (_) {
    /* quota / private mode */
  }
}

function hydrateFinishedIds() {
  readFinishedIds().forEach((id) => finishedCaptureSessionIds.add(id));
}

hydrateFinishedIds();

function normalizeSessionId(sessionId) {
  if (sessionId == null) return null;
  const sid = String(sessionId).trim();
  if (!sid || sid === "undefined" || sid === "null") return null;
  return sid;
}

function notifyListeners() {
  const ids = listParkedCaptureSessionIds();
  listeners.forEach((fn) => {
    try {
      fn(ids);
    } catch (_) {
      /* ignore */
    }
  });
}

function readMetaMap() {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(META_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMetaMap(map) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(META_STORAGE_KEY, JSON.stringify(map));
  } catch (_) {
    /* quota / private mode */
  }
}

function persistMeta(sessionId, snapshot) {
  const sid = normalizeSessionId(sessionId);
  if (!sid || !snapshot) return;
  const map = readMetaMap();
  map[sid] = {
    sessionId: sid,
    transcriptPreview: String(snapshot.transcript || "").slice(0, 240),
    elapsedSeconds: snapshot.elapsedSeconds || 0,
    lang: snapshot.lang || null,
    startOptions: snapshot.startOptions
      ? {
          audioSourceMode: snapshot.startOptions.audioSourceMode,
          // Do not persist tokens in sessionStorage
          hasAzureStreaming: Boolean(snapshot.startOptions.azureStreaming),
        }
      : null,
    liveTranscribed: snapshot.liveTranscribed !== false,
    mode: snapshot.mode || "transcribing",
    parkedAt: Date.now(),
  };
  writeMetaMap(map);
}

function removeMeta(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  const map = readMetaMap();
  if (map[sid]) {
    delete map[sid];
    writeMetaMap(map);
  }
}

/**
 * @param {string} sessionId
 * @param {object} snapshot
 */
export function isCaptureFinished(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return false;
  return finishedCaptureSessionIds.has(sid);
}

/**
 * Stop / SOAP completed for this visit — never restore or re-park it.
 */
export function markCaptureFinished(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  finishedCaptureSessionIds.add(sid);
  writeFinishedIds();
  parkedBySessionId.delete(sid);
  removeMeta(sid);
  notifyListeners();
}

/** Allow parking again if a new live capture starts on this visit. */
export function clearCaptureFinished(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  if (!finishedCaptureSessionIds.has(sid)) return;
  finishedCaptureSessionIds.delete(sid);
  writeFinishedIds();
}

export function setParkedCapture(sessionId, snapshot) {
  const sid = normalizeSessionId(sessionId);
  if (!sid || !snapshot) return;
  if (finishedCaptureSessionIds.has(sid)) return;
  parkedBySessionId.set(sid, {
    ...snapshot,
    sessionId: sid,
    parkedAt: Date.now(),
  });
  persistMeta(sid, snapshot);
  notifyListeners();
}

/**
 * @param {string} sessionId
 * @returns {object | null}
 */
export function getParkedCapture(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return null;
  if (finishedCaptureSessionIds.has(sid)) return null;
  return parkedBySessionId.get(sid) || null;
}

/**
 * Metadata-only recovery when in-memory blobs are gone.
 * @param {string} sessionId
 * @returns {object | null}
 */
export function getParkedCaptureMeta(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return null;
  if (finishedCaptureSessionIds.has(sid)) return null;
  const map = readMetaMap();
  return map[sid] || null;
}

/**
 * @param {string} sessionId
 * @returns {boolean}
 */
export function hasParkedCapture(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return false;
  if (finishedCaptureSessionIds.has(sid)) return false;
  if (parkedBySessionId.has(sid)) return true;
  const map = readMetaMap();
  return Boolean(map[sid]);
}

/**
 * @param {string} sessionId
 */
export function clearParkedCapture(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  parkedBySessionId.delete(sid);
  removeMeta(sid);
  notifyListeners();
}

/**
 * @returns {string[]}
 */
export function listParkedCaptureSessionIds() {
  const fromMemory = Array.from(parkedBySessionId.keys());
  const fromMeta = Object.keys(readMetaMap());
  return Array.from(new Set([...fromMemory, ...fromMeta])).filter(
    (id) => !finishedCaptureSessionIds.has(id)
  );
}

/**
 * Subscribe to parked-session id changes (for sidebar badges).
 * @param {(ids: string[]) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeParkedCaptures(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  try {
    listener(listParkedCaptureSessionIds());
  } catch (_) {}
  return () => listeners.delete(listener);
}
